import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { FaCheck } from "react-icons/fa";
import { RiLockLine } from "react-icons/ri";
import QuickNoteDialog from "@/components/custom-calendar/quick-note-dialog";
import Button from "@/components/ui/button";
import { useTenant } from "@/contexts/tenant-provider";
// Patient context removed; keep component local state only
import { useUser } from "@/contexts/user-provider";
import { useCarePlansForPatient } from "@/hooks/use-care-plans-for-patient";
import { useAppointmentFormStatusList } from "@/hooks/use-form-status-list";
import { useModule } from "@/hooks/use-module";
import { formatBlockedByMessage, getIncompleteDependencies } from "@/lib/checklist-dependencies";
import { formatDate, formatStaffName, formatTime } from "@/lib/format-data";
import { PERMISSIONS, hasAppointmentChecklistPermission, hasPermission } from "@/lib/permissions";
import { cx } from "@/lib/tailwind-utils";
import { shouldShowFilledFormChecklistItem } from "@/lib/tenant-flags";
import { useToast } from "@/lib/use-toast";
import { AppointmentChecklistType as ChecklistType } from "@/types/enums/appointment";
import { UserType } from "@/types/enums/user-type";
import { AppointmentChecklistItem, EventDetail, getServiceSegments } from "@/types/event";
import { UserTiny } from "@/types/user";
import ChecklistItemAction from "./checklist-item-action";
import { getFormChecklistAutoAction } from "./form-checklist-auto-action";
import { formQueryOptions } from "@/api/forms/form-query";
import { serviceQueryOptions } from "@/api/provider/admin/service-query";
import { useUpdateAppointmentChecklistMutation } from "@/query/check-in/calendar/event-query";
import { eventQueryOptions } from "@/query/check-in/calendar/event-query";
export const appointmentChecklistSkeleton = (className?: string) => <div className={cx("mt-3", className)}>
    <ul className="ml-1 space-y-3">
      {[1, 2, 3].map((item, index) => <li key={item} className="relative flex gap-x-3">
          {index < 2 && <div className="absolute left-2 top-4 h-[calc(100%+12px)] w-[1px] border-l" />}
          <div className="relative z-10 flex h-4 w-4 flex-none items-center justify-center rounded-full border bg-background/50 backdrop-blur-lg" />
          <div className="flex flex-col">
            <div className="h-4 w-40 animate-pulse rounded bg-muted" />
            <div className="mt-1 h-6 w-24 animate-pulse rounded bg-muted" />
          </div>
        </li>)}
    </ul>
  </div>;
interface AppointmentChecklistComponentProps {
  event: EventDetail;
  className?: string;
  handleViewNote?: (eventId?: string, noteId?: string) => void;
}
export function AppointmentChecklistComponent(t0) {
  const $ = _c(101);
  const {
    event,
    className,
    handleViewNote
  } = t0;
  const {
    toast
  } = useToast();
  const {
    currentUser
  } = useUser();
  const {
    tenant
  } = useTenant();
  const visualDependenciesEnabled = useModule("CHECKLIST_DEPENDENCIES");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      ...serviceQueryOptions.list(),
      select: _temp2
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    data: servicesById
  } = useQuery(t1);
  let t2;
  if ($[1] !== event.attendees) {
    t2 = event.attendees?.find(_temp3)?.user;
    $[1] = event.attendees;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const patient = t2;
  const patientId = patient?.id;
  let t3;
  if ($[3] !== patientId) {
    t3 = formQueryOptions.submissions(patientId || "");
    $[3] = patientId;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = !!patientId;
  let t5;
  if ($[5] !== t3 || $[6] !== t4) {
    t5 = {
      ...t3,
      enabled: t4
    };
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const {
    data: formSubmissions
  } = useQuery(t5);
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      ...formQueryOptions.table(),
      select: _temp5
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const {
    data: activeForms
  } = useQuery(t6);
  const completeChecklistMutation = useUpdateAppointmentChecklistMutation(servicesById);
  const formsAutoCompleted = useRef(false);
  const formsAutoCompletionAttempted = useRef(false);
  const carePlanAutoCompleted = useRef(false);
  let t7;
  if ($[9] !== event.id) {
    t7 = eventQueryOptions.appointmentChecklist(event.id);
    $[9] = event.id;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t7) {
    t8 = {
      ...t7
    };
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const {
    data: checklistData,
    isLoading: isChecklistLoading
  } = useQuery(t8);
  let t9;
  if ($[13] !== checklistData) {
    t9 = checklistData?.some(_temp6);
    $[13] = checklistData;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const hasCarePlanChecklist = t9;
  const t10 = !!patientId && !!hasCarePlanChecklist;
  let t11;
  if ($[15] !== t10) {
    t11 = {
      enabled: t10
    };
    $[15] = t10;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const {
    data: carePlans
  } = useCarePlansForPatient(patientId, t11);
  const {
    items: formStatusList,
    hasRequiredForms,
    isCoverageLoading: isFormCoverageLoading
  } = useAppointmentFormStatusList(event, patientId, formSubmissions, activeForms);
  let t12;
  if ($[17] !== currentUser) {
    t12 = type => hasAppointmentChecklistPermission(currentUser, type);
    $[17] = currentUser;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const canUpdateChecklistItem = t12;
  let t13;
  if ($[19] !== currentUser) {
    t13 = hasPermission(currentUser, PERMISSIONS.PATIENTS_PROFILES_WRITE);
    $[19] = currentUser;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const canWriteMemos = t13;
  let t14;
  if ($[21] !== checklistData || $[22] !== toast) {
    t14 = item_0 => {
      toast({
        title: "Step locked",
        description: formatBlockedByMessage(getIncompleteDependencies(item_0, checklistData ?? [])),
        variant: "error"
      });
    };
    $[21] = checklistData;
    $[22] = toast;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const showBlockedToast = t14;
  let t15;
  if ($[24] !== toast) {
    t15 = () => {
      toast({
        title: "Permission needed",
        description: "You don't have permission to update this checklist item.",
        variant: "error"
      });
    };
    $[24] = toast;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  const showPermissionDeniedToast = t15;
  let t16;
  if ($[26] !== checklistData || $[27] !== completeChecklistMutation || $[28] !== currentUser || $[29] !== event.id || $[30] !== patientId || $[31] !== toast) {
    t16 = async (itemId, checked, completedByUser, serviceId, source) => {
      if (!currentUser) {
        toast({
          title: "Error",
          description: "Unable to update checklist item. Please try again.",
          variant: "error"
        });
        return;
      }
      const checklistItem = checklistData?.find(item_1 => item_1.id === itemId);
      const completingUser = checklistItem?.type === ChecklistType.FILLED_FORM && completedByUser ? completedByUser : currentUser;
      const result = await completeChecklistMutation.mutateAsync({
        appointmentChecklistId: itemId,
        eventId: event.id,
        serviceId,
        completed: checked,
        patientId,
        optimisticCompletedByUser: completingUser,
        source: source as "patient_status_panel" | "appointment_checklist" | "checklist_item_action" | "auto_form_completion" | "auto_care_plan_completion" ?? "appointment_checklist"
      });
      if (!result) {
        toast({
          title: "Error",
          description: "Failed to update checklist item. Please try again.",
          variant: "error"
        });
        return;
      }
    };
    $[26] = checklistData;
    $[27] = completeChecklistMutation;
    $[28] = currentUser;
    $[29] = event.id;
    $[30] = patientId;
    $[31] = toast;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const handleChecklistItemChange = t16;
  let t17;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => {
      formsAutoCompleted.current = false;
      formsAutoCompletionAttempted.current = false;
      carePlanAutoCompleted.current = false;
    };
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== event.id) {
    t18 = [event.id];
    $[34] = event.id;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  useEffect(t17, t18);
  let t19;
  if ($[36] !== canUpdateChecklistItem || $[37] !== checklistData || $[38] !== currentUser || $[39] !== formStatusList || $[40] !== formSubmissions || $[41] !== handleChecklistItemChange || $[42] !== hasRequiredForms || $[43] !== isFormCoverageLoading || $[44] !== patient) {
    t19 = () => {
      if (!checklistData || !formSubmissions || isFormCoverageLoading || !currentUser || !patient || formsAutoCompletionAttempted.current) {
        return;
      }
      formsAutoCompletionAttempted.current = true;
      const formsChecklistItem = checklistData.find(_temp7);
      if (!formsChecklistItem || !!formsChecklistItem.completedDate || formsAutoCompleted.current || !canUpdateChecklistItem(formsChecklistItem.type)) {
        return;
      }
      if (getFormChecklistAutoAction(formStatusList, hasRequiredForms) === "none") {
        return;
      }
      formsAutoCompleted.current = true;
      handleChecklistItemChange(formsChecklistItem.id, true, patient, undefined, "auto_form_completion");
    };
    $[36] = canUpdateChecklistItem;
    $[37] = checklistData;
    $[38] = currentUser;
    $[39] = formStatusList;
    $[40] = formSubmissions;
    $[41] = handleChecklistItemChange;
    $[42] = hasRequiredForms;
    $[43] = isFormCoverageLoading;
    $[44] = patient;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== canUpdateChecklistItem || $[47] !== checklistData || $[48] !== currentUser || $[49] !== formStatusList || $[50] !== handleChecklistItemChange || $[51] !== isFormCoverageLoading || $[52] !== patient) {
    t20 = [checklistData, formStatusList, isFormCoverageLoading, currentUser, patient, handleChecklistItemChange, canUpdateChecklistItem];
    $[46] = canUpdateChecklistItem;
    $[47] = checklistData;
    $[48] = currentUser;
    $[49] = formStatusList;
    $[50] = handleChecklistItemChange;
    $[51] = isFormCoverageLoading;
    $[52] = patient;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  useEffect(t19, t20);
  let t21;
  let t22;
  if ($[54] !== canUpdateChecklistItem || $[55] !== carePlans || $[56] !== checklistData || $[57] !== currentUser || $[58] !== handleChecklistItemChange || $[59] !== patient) {
    t21 = () => {
      if (!checklistData || !carePlans || !currentUser || !patient || carePlanAutoCompleted.current) {
        return;
      }
      const carePlanChecklistItem = checklistData.find(_temp8);
      if (!carePlanChecklistItem || !!carePlanChecklistItem.completedDate || carePlans.length === 0 || !canUpdateChecklistItem(carePlanChecklistItem.type)) {
        return;
      }
      carePlanAutoCompleted.current = true;
      handleChecklistItemChange(carePlanChecklistItem.id, true, patient, undefined, "auto_care_plan_completion");
    };
    t22 = [checklistData, carePlans, currentUser, patient, handleChecklistItemChange, canUpdateChecklistItem];
    $[54] = canUpdateChecklistItem;
    $[55] = carePlans;
    $[56] = checklistData;
    $[57] = currentUser;
    $[58] = handleChecklistItemChange;
    $[59] = patient;
    $[60] = t21;
    $[61] = t22;
  } else {
    t21 = $[60];
    t22 = $[61];
  }
  useEffect(t21, t22);
  let t23;
  if ($[62] !== event.segments) {
    t23 = event.segments || [];
    $[62] = event.segments;
    $[63] = t23;
  } else {
    t23 = $[63];
  }
  let t24;
  if ($[64] !== t23) {
    t24 = getServiceSegments(t23);
    $[64] = t23;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  const hasMultipleServices = t24.length > 1;
  let t25;
  if ($[66] !== checklistData || $[67] !== hasRequiredForms || $[68] !== tenant) {
    let t26;
    if ($[70] !== hasRequiredForms || $[71] !== tenant) {
      t26 = item_4 => item_4.type === ChecklistType.FILLED_FORM ? shouldShowFilledFormChecklistItem({
        tenant,
        hasRequiredForms
      }) : true;
      $[70] = hasRequiredForms;
      $[71] = tenant;
      $[72] = t26;
    } else {
      t26 = $[72];
    }
    t25 = [...(checklistData || [])].sort(_temp9).filter(t26);
    $[66] = checklistData;
    $[67] = hasRequiredForms;
    $[68] = tenant;
    $[69] = t25;
  } else {
    t25 = $[69];
  }
  const sortedItems = t25;
  if (isChecklistLoading) {
    let t26;
    if ($[73] !== className) {
      t26 = appointmentChecklistSkeleton(className);
      $[73] = className;
      $[74] = t26;
    } else {
      t26 = $[74];
    }
    return t26;
  }
  if (!checklistData || checklistData.length === 0) {
    let t26;
    if ($[75] !== className) {
      t26 = cx("mt-2 text-caption text-muted-foreground", className);
      $[75] = className;
      $[76] = t26;
    } else {
      t26 = $[76];
    }
    let t27;
    if ($[77] !== t26) {
      t27 = <p className={t26}>This appointment has no checklist items.</p>;
      $[77] = t26;
      $[78] = t27;
    } else {
      t27 = $[78];
    }
    return t27;
  }
  let t26;
  if ($[79] !== className) {
    t26 = cx(className);
    $[79] = className;
    $[80] = t26;
  } else {
    t26 = $[80];
  }
  let t27;
  if ($[81] !== canUpdateChecklistItem || $[82] !== canWriteMemos || $[83] !== checklistData || $[84] !== event || $[85] !== handleChecklistItemChange || $[86] !== handleViewNote || $[87] !== hasMultipleServices || $[88] !== patient || $[89] !== patientId || $[90] !== servicesById || $[91] !== showBlockedToast || $[92] !== showPermissionDeniedToast || $[93] !== sortedItems || $[94] !== visualDependenciesEnabled) {
    t27 = sortedItems.map((item_5, index) => {
      const serviceId_0 = item_5.serviceId as string | undefined;
      const prevServiceId = index > 0 ? sortedItems[index - 1]?.serviceId as string | undefined : undefined;
      const isServiceHeader = !!serviceId_0 && serviceId_0 !== prevServiceId;
      const canUpdateItem = canUpdateChecklistItem(item_5.type);
      const blockingDependencies = visualDependenciesEnabled ? getIncompleteDependencies(item_5, checklistData) : [];
      const blocked = blockingDependencies.length > 0;
      const isCompleted = !!item_5.completedDate && !blocked;
      const canToggle = canUpdateItem && (!blocked || !!item_5.completedDate);
      return <div key={item_5.serviceId + item_5.id}>{isServiceHeader && hasMultipleServices && <div className="relative flex flex-col" key={item_5.serviceId}>{index < sortedItems.length - 1 && <div className="absolute left-2 top-4 h-full w-[1px] border-l border-dashed" />}<p className={cx("ml-7 text-body-emphasis uppercase leading-none tracking-wide text-muted-foreground", index !== 0 && "mt-6")}>{servicesById?.get(serviceId_0 || "")?.name || "Service"}</p></div>}<li key={item_5.id} className="relative flex gap-x-3 space-y-3">{index < sortedItems.length - 1 && <div className="absolute left-2 top-4 h-full w-[1px] border-l" />}<div className={cx("z-10 mt-3 flex h-4 w-4 flex-none items-center justify-center rounded-full border bg-background/50 backdrop-blur-sm", canToggle ? "cursor-pointer" : "cursor-not-allowed opacity-50", isCompleted && "border-primary")} data-testid={`checklist-toggle-${item_5.id}`} aria-disabled={!canToggle} onClick={() => {
            if (!canUpdateItem) {
              showPermissionDeniedToast();
              return;
            }
            if (blocked && !item_5.completedDate) {
              showBlockedToast(item_5);
              return;
            }
            handleChecklistItemChange(item_5.id, !item_5.completedDate);
          }}>{isCompleted && <FaCheck className="h-2 w-2 text-primary" />}{blocked && <RiLockLine className="h-2.5 w-2.5 text-foreground-muted" />}</div><div className="flex min-w-0 flex-1 flex-col overflow-hidden"><p className="-mt-0.5 py-0 text-body-strong text-foreground-secondary">{item_5.name}{item_5.optional && <span className="ml-1 text-caption text-foreground-muted">(optional)</span>}</p>{blocked ? <p className="mt-1 flex items-center gap-1 text-caption text-foreground-muted" data-testid={`checklist-blocked-${item_5.id}`}><RiLockLine className="h-3 w-3 shrink-0" />{formatBlockedByMessage(blockingDependencies)}</p> : <ChecklistItemAction item={item_5} handleViewNote={handleViewNote} className="mt-1 max-w-full" event={event} patientId={patientId} servicesById={servicesById} canUpdateItem={canUpdateItem} onPermissionDenied={showPermissionDeniedToast} />}{!blocked && item_5.memoEnabled && canWriteMemos && canUpdateItem && patient && <QuickNoteDialog event={event} patient={patient} onNoteCreate={() => {
              if (!item_5.completedDate) {
                handleChecklistItemChange(item_5.id, true, undefined, item_5.serviceId, "checklist_item_action");
              }
            }}><Button variant="secondary" size="sm" className="mt-1 w-fit">Add Memo</Button></QuickNoteDialog>}{isCompleted && item_5.completedDate && (() => {
              const today = new Date();
              const completedDate = new Date(item_5.completedDate);
              const isToday = completedDate.getUTCFullYear() === today.getUTCFullYear() && completedDate.getUTCMonth() === today.getUTCMonth() && completedDate.getUTCDate() === today.getUTCDate();
              return <p className="text-caption text-muted-foreground"><span>Completed</span>{item_5.completedByUser && <span>{` by ${formatStaffName(item_5.completedByUser)}`}</span>}{isToday ? <span>{` at ${formatTime(item_5.completedDate)}`}</span> : <span>{` on ${formatDate(item_5.completedDate)}`}</span>}</p>;
            })()}</div></li></div>;
    });
    $[81] = canUpdateChecklistItem;
    $[82] = canWriteMemos;
    $[83] = checklistData;
    $[84] = event;
    $[85] = handleChecklistItemChange;
    $[86] = handleViewNote;
    $[87] = hasMultipleServices;
    $[88] = patient;
    $[89] = patientId;
    $[90] = servicesById;
    $[91] = showBlockedToast;
    $[92] = showPermissionDeniedToast;
    $[93] = sortedItems;
    $[94] = visualDependenciesEnabled;
    $[95] = t27;
  } else {
    t27 = $[95];
  }
  let t28;
  if ($[96] !== t27) {
    t28 = <ul className="ml-1">{t27}</ul>;
    $[96] = t27;
    $[97] = t28;
  } else {
    t28 = $[97];
  }
  let t29;
  if ($[98] !== t26 || $[99] !== t28) {
    t29 = <div className={t26}>{t28}</div>;
    $[98] = t26;
    $[99] = t28;
    $[100] = t29;
  } else {
    t29 = $[100];
  }
  return t29;
}
function _temp9(a_0, b) {
  return a_0.position - b.position;
}
function _temp8(item_3) {
  return item_3.type === ChecklistType.CARE_PLAN;
}
function _temp7(item_2) {
  return item_2.type === ChecklistType.FILLED_FORM;
}
function _temp6(item) {
  return item.type === ChecklistType.CARE_PLAN;
}
function _temp5(data_0) {
  return new Set((data_0 ?? []).map(_temp4));
}
function _temp4(f) {
  return f.id;
}
function _temp3(a) {
  return a.user?.type === UserType.PATIENT;
}
function _temp2(data) {
  return new Map(data?.map(_temp));
}
function _temp(service) {
  return [service.id, service];
}